/*
 * @providesModule LiveBracketAutoplayDialog
 *
 */

import React, {useEffect, useRef, useMemo} from 'react';
import {Animated, Text} from 'react-native';
import moment from 'moment';
import LinearGradient from 'react-native-linear-gradient';

import styles from './LiveBracketAutoPlayDialog.styles';
import {DialogV2} from '@skillz-rn/atoms';
import {_m} from 'INTL';
import SkillzImage from 'SkillzImage';
import ClientPropertyConstants from 'ClientPropertyConstants';
import Tournament from 'SKZTournament';
import Label from 'Label';
import {LiveBracketTimerUtils} from 'LiveBracketTimerUtils';

const FADE_ANIMATION_DURATION_MS = 350;
const TOGGLE_TEXT_INTERVAL_MS = 2000;
const TEXT_FADE_OUT_TIMEOUT_MS = 1500;

const SECONDARY_GRADIENT_COLORS = ['#8B070A', '#D80C0F', '#F96568', '#8E080A'];
const SECONDARY_GRADIENT_LOCATIONS = [0, 0.25, 0.5, 1];

type LiveBracketAutoplayDialogProps = {
  tournamentData?: Tournament;
};

export default function LiveBracketAutoplayDialog({
  tournamentData,
}: LiveBracketAutoplayDialogProps) {
  const tournamentName = tournamentData?.name;
  const roundToJoin = tournamentData?.bracket?.current_round;
  const [currentText, setCurrentText] = React.useState('');
  const [secondaryRibbonText, setSecondaryRibbonText] = React.useState('');
  const animation = useRef(new Animated.Value(0));
  const list = [
    _m('Gathering the players...'),
    _m('Counting the rewards...'),
    _m('Setting up the matches...'),
  ];

  const countToDate = useMemo(() => {
    return new Date(tournamentData?.bracket?.current_round_end_time ?? -1);
  }, [tournamentData?.bracket?.current_round_end_time]);

  const tournamentTimerLengthSeconds = ClientPropertyConstants.getTournamentTimerLengthSeconds();

  const calculateRibbonText = React.useCallback(() => {
    const totalRemainingTime = moment(countToDate).diff(moment(), 'seconds');
    const safeRoundToJoin = roundToJoin && !isNaN(roundToJoin) ? roundToJoin : 1;
    if (
      safeRoundToJoin >= 1 &&
      totalRemainingTime > 0 &&
      totalRemainingTime < tournamentTimerLengthSeconds
    ) {
      const formattedTime = LiveBracketTimerUtils.formatTimeMMSS(Math.max(0, totalRemainingTime));
      return `${formattedTime} LEFT UNTIL ROUND ${safeRoundToJoin} ENDS!`;
    }
    return '';
  }, [countToDate, tournamentTimerLengthSeconds, roundToJoin]);

  useEffect(() => {
    let counter = 0;
    showAnimatedText(list[0] || '');
    const interval = setInterval(() => {
      counter = (counter + 1) % list.length;
      showAnimatedText(list[counter] || '');
    }, TOGGLE_TEXT_INTERVAL_MS);

    return () => {
      interval && clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    setSecondaryRibbonText(calculateRibbonText());

    const interval = setInterval(() => {
      setSecondaryRibbonText(calculateRibbonText());
    }, 1000);

    return () => {
      clearInterval(interval);
    };
  }, [calculateRibbonText]);

  function showAnimatedText(item: string) {
    fadeIn();
    setCurrentText(item);

    setTimeout(() => {
      fadeOut();
    }, TEXT_FADE_OUT_TIMEOUT_MS);
  }

  function fadeIn() {
    Animated.timing(animation.current, {
      toValue: 1,
      duration: FADE_ANIMATION_DURATION_MS,
      useNativeDriver: true,
    }).start();
  }

  function fadeOut() {
    Animated.timing(animation.current, {
      toValue: 0,
      duration: FADE_ANIMATION_DURATION_MS,
      useNativeDriver: true,
    }).start();
  }

  return (
    <DialogV2
      title={_m('TOURNAMENT STARTING NOW!')}
      shouldShowRibbon={true}
      icon={
        <SkillzImage
          resizeMode={'contain'}
          source={'ic_live_brackets_cup.png'}
          style={{width: 125, height: 125}}
          fallback={true}
        />
      }
      descriptionView={
        <>
          <Text style={styles.regularDescriptionStyle}>
            <Text style={styles.boldDescriptionStyle}>{tournamentName + ' '}</Text>
            {_m('will auto-play soon.')}
          </Text>
          <Animated.Text
            numberOfLines={1}
            style={{
              ...styles.liveBracketDialogText,
              opacity: animation.current,
            }}
          >
            {currentText}
          </Animated.Text>
          {secondaryRibbonText && (
            <>
              <LinearGradient
                colors={SECONDARY_GRADIENT_COLORS}
                locations={SECONDARY_GRADIENT_LOCATIONS}
                start={{x: 0, y: 0}}
                end={{x: 1, y: 0}}
                style={styles.countdownStrip}
              >
                <Label style={styles.countdownText}>{secondaryRibbonText}</Label>
              </LinearGradient>
              <Label style={styles.warningText}>{_m("Submit on time or you'll be aborted!")}</Label>
            </>
          )}
        </>
      }
      showActionButton={false}
    />
  );
}
